import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Button, Input, Select, Table, Tag} from 'antd'
import {getAllSkuCoverage, getCoverageSummary, getStages} from '../../../../services/api/apiProductionPlanning'
import {EXTERNAL_ROUTES, ROUTES, stageConfigPath} from '../../constants'
import {formatInt} from '../../helpers/format'
import {gotoSkuDetail} from '../../helpers/skuDetail'
import useUrlFilters from '../../../helpers/useUrlFilters'
import Notice from '../shared/Notice'
import SkuIdList from '../shared/SkuIdList'
import LoadError from '../shared/LoadError'

const {Option} = Select

/**
 * SCR-M16-003 — SKU thiếu định mức.
 *
 * Hai loại vấn đề được tách hẳn ra (BR-M16-27): SKU **thiếu định mức** và SKU **chưa gán quy trình**.
 * W1 (chưa gán quy trình) đặt TRÊN W2 (thiếu định mức) vì chưa có quy trình thì không biết cần cấu
 * hình những công đoạn nào, và nút hành động của hai loại dẫn sang hai màn khác nhau.
 *
 * SKU trạng thái Tắt bị loại ngay từ bước truy vấn, không xuất hiện ở bất kỳ thẻ thống kê hay cảnh
 * báo nào (BR-M16-26).
 */
/**
 * Bộ lọc nằm trên URL để copy link gửi cho người khác là họ mở ra thấy đúng cái mình đang xem.
 * Tên khoá giữ đúng như cũ (`routing`, `stage`, `status`) vì cảnh báo W1/W2 ở SCR-M16-001 đang
 * dẫn sang đây bằng chính hai khoá đó.
 */
const FILTER_DEFAULTS = {
    routing: 'all',
    stage: 'all',
    status: 'missing',
    sku: '',
}

function MissingSkuPage({history, location}) {
    const [summary, setSummary] = useState(null)
    const [rows, setRows] = useState([])
    // Danh sách bị cắt vì chạm trần số trang — xem `getAllSkuCoverage`. Gần như không bao giờ xảy ra,
    // nhưng nếu xảy ra thì màn này lọc/đếm trên tập THIẾU nên bắt buộc phải nói ra.
    const [truncated, setTruncated] = useState(null)
    const [stages, setStages] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // Bộ lọc đặt sẵn khi vào từ W1 / W2 của SCR-M16-001, và ghi ngược lên URL khi đổi.
    const [filters, setFilter, resetFilters, filtersDirty] = useUrlFilters(FILTER_DEFAULTS, {history, location})
    const {routing, stage: missingStage, status, sku: skuKeyword} = filters
    // Mã SKU đang tra `_id` để mở màn chi tiết — dùng để khoá đúng nút vừa bấm.
    const [openingSku, setOpeningSku] = useState(null)

    const fetchData = useCallback(async () => {
        setLoading(true)
        setError(null)

        try {
            // Lấy ĐỦ mọi trang: màn này lọc và đếm ở trình duyệt nên thiếu dòng là ra kết quả sai.
            const [coverage, list, stageRows] = await Promise.all([
                getCoverageSummary(),
                getAllSkuCoverage(),
                getStages(),
            ])

            setSummary(coverage)
            setRows(list.rows)
            setTruncated(list.truncated
                ? {shown: list.rows.length, total: typeof list.total === 'number' ? list.total : null}
                : null)
            setStages(stageRows || [])
        } catch (e) {
            setError(e)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const routingOptions = useMemo(() => {
        const map = {}

        rows.forEach((row) => {
            if (row.routingCode) map[row.routingCode] = row.routingName || row.routingCode
        })

        return Object.keys(map).map((code) => ({code, name: map[code]}))
    }, [rows])

    /** Bộ lọc áp dụng NGAY khi thay đổi, không có nút "Áp dụng" (§3.3). */
    const filtered = useMemo(() => rows.filter((row) => {
        if (routing === '__none__' && row.routingCode) return false
        if (routing !== 'all' && routing !== '__none__' && row.routingCode !== routing) return false
        if (missingStage !== 'all' && !(row.missingStageCodes || []).includes(missingStage)) return false
        if (status === 'missing' && row.status === 'covered') return false
        if (skuKeyword && !row.skuId.toLowerCase().includes(skuKeyword.trim().toLowerCase())) return false

        return true
    }), [rows, routing, missingStage, status, skuKeyword])

    const openSkuDetail = async (skuId) => {
        setOpeningSku(skuId)

        try {
            await gotoSkuDetail(history, skuId)
        } finally {
            setOpeningSku(null)
        }
    }

    const stageNameOf = (code) => {
        const found = stages.find((item) => item.stageCode === code)

        return found ? found.name : code
    }

    const columns = [
        {
            title: 'SKU',
            dataIndex: 'skuId',
            width: 250,
            render: (skuId) => <code className="VcSkuCode">{skuId}</code>,
        },
        {
            title: 'Quy trình sản xuất',
            key: 'routing',
            width: 130,
            render: (text, row) => (row.routingCode
                ? <Tag color="blue">{row.routingName || row.routingCode}</Tag>
                : <Tag color="red">–</Tag>),
        },
        {
            title: 'Nguyên vật liệu cấu thành',
            key: 'materials',
            width: 200,
            render: (text, row) => (row.materials || []).map((item) => (
                <Tag color="purple" key={item.materialCode}>{item.name}</Tag>
            )),
        },
        {
            title: 'Công đoạn cần có định mức',
            key: 'stages',
            render: (text, row) => {
                if (row.status === 'missing_routing') {
                    return (
                        <Tag color="orange">Chưa gán quy trình — không xác định được công đoạn</Tag>
                    )
                }

                // KHÔNG liệt kê tên NVL trên thẻ đỏ (D-CR-14). Cảnh báo đo ở mức công đoạn: công
                // đoạn được coi là đủ khi có ít nhất một nhóm khớp. Liệt kê "thiếu NVL nào" sẽ tạo
                // cảnh báo sai trên cấu hình đúng, vì gỗ chỉ cắt mà không in.
                return (row.stages || []).map((item) => (
                    <span
                        key={item.stageCode}
                        className={[
                            'VcStageChip',
                            item.covered ? 'VcStageChip--ok' : 'VcStageChip--missing',
                            item.source === 'mandatory' ? 'VcStageChip--mandatory' : '',
                        ].join(' ')}
                    >
                        {item.name}{item.covered ? '' : ' ✗'}
                    </span>
                ))
            },
        },
        {
            title: '',
            key: 'action',
            width: 170,
            render: (text, row) => {
                if (row.status === 'missing_routing') {
                    return (
                        <Button
                            size="small"
                            loading={openingSku === row.skuId}
                            disabled={!!openingSku && openingSku !== row.skuId}
                            onClick={() => openSkuDetail(row.skuId)}
                        >
                            Gán quy trình
                        </Button>
                    )
                }

                if (!(row.missingStageCodes || []).length) {
                    return <span className="VcSuccess">Đủ định mức</span>
                }

                const first = row.missingStageCodes[0]

                return (
                    <Button
                        size="small"
                        type="primary"
                        onClick={() => history.push(stageConfigPath(first))}
                    >
                        Cấu hình {stageNameOf(first)}
                    </Button>
                )
            },
        },
    ]

    const cards = summary ? [
        {label: 'SKU đang bật', value: summary.activeSkuCount, tone: ''},
        {label: 'Đủ định mức', value: summary.coveredSkuCount, tone: 'ok'},
        {label: 'Thiếu ít nhất 1 công đoạn', value: summary.incompleteSkuCount, tone: 'bad'},
        {label: 'Chưa gán quy trình sản xuất', value: summary.missingRoutingSkuCount, tone: 'warn'},
    ] : []

    // C2 + C3 + C4 phải bằng C1. Không khớp là lỗi logic phía service (E-303): vẫn hiện bảng nhưng
    // ghi cảnh báo cho Dev, không im lặng.
    if (summary) {
        const sum = summary.coveredSkuCount + summary.incompleteSkuCount + summary.missingRoutingSkuCount

        if (sum !== summary.activeSkuCount) {
            // eslint-disable-next-line no-console
            console.warn('[M16][E-303] C2 + C3 + C4 ≠ C1', summary)
        }
    }

    return (
        <div className="VcPage VcMissingSkuPage container-fluid">
            <div className="VcPageHead">
                <h1 className="VcPageTitle">SKU thiếu định mức</h1>
                <div className="VcPageActions">
                    <Button onClick={() => history.push(ROUTES.STANDARD_TIME_STAGES)}>← Về danh sách công đoạn</Button>
                </div>
            </div>

            {error ? (
                <LoadError error={error} message="Không tải được dữ liệu độ phủ. Vui lòng thử lại" onRetry={fetchData}/>
            ) : (
                <>
                    <div className="VcStatCards">
                        {cards.map((card) => (
                            <div className={`VcStatCard VcStatCard--${card.tone}`} key={card.label}>
                                <div className="VcStatLabel">{card.label}</div>
                                <div className="VcStatValue">{formatInt(card.value)}</div>
                            </div>
                        ))}
                    </div>

                    {summary && summary.missingRoutingSkuCount > 0 && (
                        <Notice
                            level="warning"
                            action="Mở màn Quản lý SKU →"
                            onAction={() => history.push(EXTERNAL_ROUTES.SKU_MANAGER)}
                        >
                            <div>
                                {formatInt(summary.missingRoutingSkuCount)} SKU đang bật chưa gán quy
                                trình sản xuất. Không xác định được cần định mức những công đoạn nào —
                                xử lý ở màn Quản lý SKU.
                            </div>
                            <SkuIdList ids={summary.missingRoutingSkuIds}/>
                        </Notice>
                    )}

                    {summary && summary.incompleteSkuCount > 0 && (
                        <Notice level="error">
                            {formatInt(summary.incompleteSkuCount)} SKU đang bật thiếu định mức. Rơi vào
                            no_standard ở công đoạn tương ứng.
                        </Notice>
                    )}

                    {summary && !summary.incompleteSkuCount && !summary.missingRoutingSkuCount && (
                        <Notice level="success">Toàn bộ SKU đang bật đã đủ định mức.</Notice>
                    )}

                    {truncated && (
                        <Notice level="error">
                            {/*
                              * Hai câu ĐẦY ĐỦ, không ghép đuôi chung. Ghép thì nhánh `null` kết bằng
                              * dấu chấm rồi vẫn bị nối "danh sách bị cắt…" viết thường — câu gãy làm
                              * banner trông như lỗi render, đúng chỗ nó đang bảo người dùng báo Dev.
                              */}
                            {truncated.total === null ? (
                                <>
                                    Chỉ tải được <strong>{formatInt(truncated.shown)}</strong> SKU rồi
                                    dừng ở trần số trang, và service không trả tổng số nên không biết
                                    còn thiếu bao nhiêu.
                                </>
                            ) : (
                                <>
                                    Chỉ tải được <strong>{formatInt(truncated.shown)}</strong> trên{' '}
                                    <strong>{formatInt(truncated.total)}</strong> SKU — danh sách bị cắt
                                    ở trần số trang.
                                </>
                            )}{' '}
                            Bảng và các bộ lọc bên dưới đang tính trên phần đã tải, KHÔNG phải trên
                            toàn bộ danh mục.
                            <div className="VcNoticeSub">
                                Đây là dấu hiệu bất thường: danh mục lớn hơn nhiều so với dự kiến, hoặc
                                phân trang phía service trả sai số trang. Báo Dev trước khi dùng số ở màn này.
                            </div>
                        </Notice>
                    )}

                    <div className="VcFilterBar">
                        <div className="VcFilterItem">
                            <label>Quy trình sản xuất</label>
                            <Select value={routing} onChange={(value) => setFilter({routing: value})} style={{width: 200}}>
                                <Option value="all">Tất cả</Option>
                                {routingOptions.map((item) => (
                                    <Option key={item.code} value={item.code}>{item.name}</Option>
                                ))}
                                <Option value="__none__">(chưa gán)</Option>
                            </Select>
                        </div>
                        <div className="VcFilterItem">
                            <label>Công đoạn thiếu</label>
                            <Select value={missingStage} onChange={(value) => setFilter({stage: value})} style={{width: 180}}>
                                <Option value="all">Tất cả</Option>
                                {stages
                                    .filter((item) => item.stageType !== 'on_demand')
                                    .map((item) => (
                                        <Option key={item.stageCode} value={item.stageCode}>{item.name}</Option>
                                    ))}
                            </Select>
                        </div>
                        <div className="VcFilterItem">
                            <label>Tình trạng</label>
                            <Select value={status} onChange={(value) => setFilter({status: value})} style={{width: 220}}>
                                <Option value="missing">Chỉ SKU đang thiếu</Option>
                                <Option value="all">Tất cả SKU đang bật</Option>
                            </Select>
                        </div>
                        <div className="VcFilterItem">
                            <label>Mã SKU</label>
                            <Input
                                allowClear
                                value={skuKeyword}
                                placeholder="Lọc ngay khi gõ"
                                onChange={(event) => setFilter({sku: event.target.value})}
                                style={{width: 200}}
                            />
                        </div>

                        {/*
                          * Bộ lọc nằm trên URL nên nó sống qua refresh và nút Back — mở lại màn vẫn
                          * thấy lựa chọn cũ. Nút này là đường về "Tất cả" trong một lần bấm, và chỉ
                          * hiện khi thật sự có gì để đặt lại.
                          */}
                        {filtersDirty && (
                            <div className="VcFilterItem VcFilterItem--action">
                                <Button onClick={resetFilters}>Đặt lại bộ lọc</Button>
                            </div>
                        )}
                    </div>

                    <Table
                        className="VcTable"
                        rowKey="skuId"
                        columns={columns}
                        dataSource={filtered}
                        loading={loading}
                        // Trước chỉ có 50 dòng nên không cần phân trang; giờ tải đủ 377 và bộ lọc
                        // mặc định giữ lại 367, dựng hết một lượt là màn hình ì hẳn.
                        pagination={{
                            pageSize: 50,
                            showSizeChanger: true,
                            pageSizeOptions: ['50', '100', '200'],
                            showTotal: (total) => `${formatInt(total)} SKU khớp bộ lọc`,
                        }}
                        size="middle"
                        locale={{emptyText: 'Không có SKU nào khớp bộ lọc'}}
                    />
                </>
            )}
        </div>
    )
}

export default MissingSkuPage
